/**
 * Cloud file sources for message attachments: Google Drive (official Google
 * Picker) and Microsoft OneDrive / SharePoint (an in-app browser over the
 * backend's Graph proxy).
 *
 * Endpoints (TRUS-638):
 *   GET  /api/integrations/cloud-files/status
 *   GET  /api/integrations/cloud-files/google/picker-token
 *   GET  /api/integrations/cloud-files/microsoft/items
 *   GET  {connect_path}?redirect_path=…   (connect → { auth_url })
 *   DELETE /api/integrations/cloud-files/{google_drive|microsoft_files}
 *   GET/PUT /api/integrations/cloud-files/org-settings   (org admins)
 *
 * Everything is scoped to the active org (X-CBO-ID): connections are per
 * user + org, and each org enables providers itself (off by default). Cache
 * keys must therefore include the active org id.
 *
 * The backend never answers 401 here (that would log the user out); 409 means
 * "not connected / revoked — reconnect".
 */

import { apiClient, handleApiError } from './apiClient';

export type CloudConnectProvider = 'google_drive' | 'microsoft_files';

export interface GooglePickerConfig {
  api_key: string;
  app_id: string;
  client_id: string;
}

export interface CloudProviderStatus {
  /** The active org has turned this provider on. false ⇒ hide it entirely. */
  org_enabled: boolean;
  /** Configured (credentials present) AND org_enabled. */
  available: boolean;
  /** Email domains the org allows for this provider ([] = any). */
  allowed_domains: string[];
  connected: boolean;
  account_email: string | null;
  /** e.g. /api/integrations/cloud-files/google_drive/connect */
  connect_path: string;
}

export interface CloudFilesStatus {
  google_drive: CloudProviderStatus & {
    /** null when the workspace has no Google Picker credentials (then available is false). */
    picker: GooglePickerConfig | null;
  };
  microsoft_files: CloudProviderStatus;
}

export interface CloudOrgProviderSettings {
  enabled: boolean;
  allowed_domains: string[];
}

export interface CloudOrgSettings {
  google_drive: CloudOrgProviderSettings;
  microsoft_files: CloudOrgProviderSettings & { allowed_tenant_ids: string[] };
}

export interface CloudOrgSettingsResponse extends CloudOrgSettings {
  can_manage: boolean;
}

export interface GooglePickerToken {
  access_token: string;
  expires_at: string | null;
}

export type MicrosoftSource = 'recent' | 'shared' | 'onedrive' | 'sharepoint';

export interface MicrosoftItem {
  provider: 'onedrive' | 'sharepoint';
  /** SharePoint drill-down: site → drive (library) → folder → file. */
  item_type: 'file' | 'folder' | 'site' | 'drive';
  id: string;
  drive_id: string | null;
  site_id: string | null;
  name: string;
  web_url: string | null;
  mime_type: string | null;
  size: number | null;
  modified_at: string | null;
  is_folder: boolean;
  site_name: string | null;
  icon_hint: string | null;
}

export interface MicrosoftItemsQuery {
  source: MicrosoftSource;
  q?: string;
  site_id?: string | null;
  drive_id?: string | null;
  folder_id?: string | null;
}

function blobText(blob: Blob): Promise<string> {
  if (typeof blob.text === 'function') return blob.text();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(reader.error);
    reader.readAsText(blob);
  });
}

/** Why a cloud-file preview isn't shown to THIS viewer (their own access decides). */
export type CloudPreviewReason = 'not_connected' | 'no_access' | 'org_disabled' | 'no_preview';

export type CloudPreview = { available: true; blob: Blob } | { available: false; reason: CloudPreviewReason };

export const cloudFilesApi = {
  /**
   * GET /api/messaging/attachments/{id}/cloud-preview — the thumbnail fetched with
   * the viewer's own grant in the active org; JSON {available:false, reason}
   * otherwise. Never 401.
   */
  async getAttachmentPreview(attachmentId: number): Promise<CloudPreview> {
    try {
      const response = await apiClient.get(`/api/messaging/attachments/${attachmentId}/cloud-preview`, {
        responseType: 'blob',
      });
      const blob: Blob = response.data;
      if (blob.type.startsWith('image/')) return { available: true, blob };
      const body = JSON.parse(await blobText(blob));
      const reason: CloudPreviewReason =
        body?.reason === 'not_connected' || body?.reason === 'no_access' || body?.reason === 'org_disabled'
          ? body.reason
          : 'no_preview';
      return { available: false, reason };
    } catch {
      // A preview is decoration: any failure just shows the plain card.
      return { available: false, reason: 'no_preview' };
    }
  },

  async getStatus(): Promise<CloudFilesStatus> {
    try {
      const response = await apiClient.get('/api/integrations/cloud-files/status');
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Start the OAuth connect flow: GET {connect_path}?redirect_path=<in-app path>
   * → { auth_url }. After consent the provider callback sends the browser to
   * FRONTEND_URL + redirect_path (must start with "/", else 400).
   */
  async connect(connectPath: string, redirectPath?: string): Promise<{ auth_url: string }> {
    try {
      const response = await apiClient.get(connectPath, {
        params: redirectPath ? { redirect_path: redirectPath } : undefined,
      });
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  async disconnect(provider: CloudConnectProvider): Promise<{ success: boolean; revoked_at_provider: boolean }> {
    try {
      const response = await apiClient.delete(`/api/integrations/cloud-files/${provider}`);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  async getOrgSettings(): Promise<CloudOrgSettingsResponse> {
    try {
      const response = await apiClient.get('/api/integrations/cloud-files/org-settings');
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /** Returns the saved settings; `revoked` = connections removed by disabling/restricting. */
  async updateOrgSettings(settings: CloudOrgSettings): Promise<CloudOrgSettings & { revoked?: number }> {
    try {
      const response = await apiClient.put('/api/integrations/cloud-files/org-settings', settings);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  async getGooglePickerToken(): Promise<GooglePickerToken> {
    try {
      const response = await apiClient.get('/api/integrations/cloud-files/google/picker-token');
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  async listMicrosoftItems(query: MicrosoftItemsQuery): Promise<MicrosoftItem[]> {
    const params: Record<string, string> = { source: query.source };
    if (query.q) params.q = query.q;
    if (query.site_id) params.site_id = query.site_id;
    if (query.drive_id) params.drive_id = query.drive_id;
    if (query.folder_id) params.folder_id = query.folder_id;
    try {
      const response = await apiClient.get('/api/integrations/cloud-files/microsoft/items', { params });
      // { source, items: [...] } — tolerate a bare array too.
      return Array.isArray(response.data) ? response.data : response.data?.items ?? [];
    } catch (error) {
      throw handleApiError(error);
    }
  },
};

export default cloudFilesApi;
